import { useCallback } from "react";
import { researchCms } from "@/api/researchCms.service";
import type { AsyncSelectOption } from "@/components/Form/AsyncSearchableSelect/AsyncSearchableSelect";

type BpaFormSearchesOptions = {
  rankingEntityId?: string | null;
  editionId: string;
  previousEditionId: string | null;
  researchers: AsyncSelectOption[];
  editionNamesByEditionId?: Map<string, string>;
};

export function useBpaFormSearches({
  rankingEntityId,
  editionId,
  previousEditionId,
  researchers,
  editionNamesByEditionId,
}: BpaFormSearchesOptions) {
  const searchPracticeGroups = useCallback(
    async (query: string): Promise<AsyncSelectOption[]> => {
      if (!rankingEntityId) return [];
      const result = await researchCms.getPracticeGroups({
        rankingEntityId,
        search: query,
        limit: 50,
      });
      return result.researchPracticeGroups.map(pg => ({ value: pg.id, label: pg.name }));
    },
    [rankingEntityId],
  );

  const searchResearchers = useCallback(
    async (query: string): Promise<AsyncSelectOption[]> => {
      const q = query.toLowerCase();
      return researchers.filter(r => r.label.toLowerCase().includes(q));
    },
    [researchers],
  );

  const searchPreviousBpas = useCallback(
    async (query: string): Promise<AsyncSelectOption[]> => {
      if (!previousEditionId) return [];
      const result = await researchCms.getEditionBpas({
        editionId: previousEditionId,
        search: query,
        limit: 50,
      });
      return result.editionBpas.map(b => {
        const edition = editionNamesByEditionId?.get(b.editionId) ?? "";
        const parts = [b.rankingEntityName, edition].filter(Boolean).join(" · ");
        return { value: b.id, label: `${b.bpaName} — ${parts}` };
      });
    },
    [previousEditionId, editionNamesByEditionId],
  );

  const searchSourceBpas = useCallback(
    async (query: string): Promise<AsyncSelectOption[]> => {
      if (!editionId) return [];
      const result = await researchCms.getEditionBpas({ editionId, search: query, limit: 50 });
      return result.editionBpas.map(b => {
        const edition = editionNamesByEditionId?.get(b.editionId) ?? "";
        const parts = [b.rankingEntityName, edition].filter(Boolean).join(" · ");
        return { value: b.id, label: `${b.bpaName} — ${parts}` };
      });
    },
    [editionId, editionNamesByEditionId],
  );

  return { searchPracticeGroups, searchResearchers, searchPreviousBpas, searchSourceBpas };
}
